import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../i18n/translations';
import daivLogoNoText from '../assets/images/db_logo_notext.png';
import { isInAppWebView } from '../utils/inAppWebView';
import { ensureMetaPixelInit, trackMetaPageView } from '../utils/metaPixelInit';
import { shouldHideMobileChromeInAppView } from '../utils/bookPageMobileChrome';
import { isMetaPixelAllowedOnCurrentHost } from '../config/metaPixel';
import './MetaPixelConsent.css';

const STORAGE_KEY = 'daiv_marketing_consent';

function readStoredConsent() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'rejected' ? v : null;
  } catch {
    return null;
  }
}

export default function MetaPixelConsent() {
  const { lang } = useLanguage();
  const location = useLocation();
  const skipInitialPageView = useRef(true);
  const [consent, setConsent] = useState(readStoredConsent);
  const [compactRow, setCompactRow] = useState(false);
  const pixelAllowed = isMetaPixelAllowedOnCurrentHost();
  const hideConsentBanner = !pixelAllowed || shouldHideMobileChromeInAppView(location.pathname, location.search);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => {
      const webview = isInAppWebView();
      setCompactRow(webview || mq.matches);
      document.documentElement.classList.toggle('daiv-in-app-webview', webview);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.documentElement.classList.remove('daiv-in-app-webview');
    };
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setConsent(readStoredConsent());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /* Pixel loads on page load (index.js); ensure init if this mounts first. */
  useEffect(() => {
    if (!pixelAllowed) return;
    ensureMetaPixelInit();
  }, [pixelAllowed]);

  /* SPA route changes */
  useEffect(() => {
    if (typeof window === 'undefined' || !window.fbq) return;
    if (skipInitialPageView.current) {
      skipInitialPageView.current = false;
      return;
    }
    trackMetaPageView();
  }, [location.pathname, location.search]);

  const persistConsent = useCallback((value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
    setConsent(value);
  }, []);

  const accept = useCallback(() => persistConsent('accepted'), [persistConsent]);
  const reject = useCallback(() => persistConsent('rejected'), [persistConsent]);

  useEffect(() => {
    if (consent || hideConsentBanner) {
      document.documentElement.classList.remove('daiv-cookie-visible');
      return undefined;
    }
    document.documentElement.classList.add('daiv-cookie-visible');
    return () => document.documentElement.classList.remove('daiv-cookie-visible');
  }, [consent, hideConsentBanner]);

  if (consent || hideConsentBanner) return null;

  return (
    <div
      className={`daiv-cookie-banner${compactRow ? ' daiv-cookie-banner--compact' : ''}`}
      role="dialog"
      aria-labelledby="daiv-cookie-banner-title"
      aria-live="polite"
    >
      <div className="daiv-cookie-banner-inner">
        <div className="daiv-cookie-banner-brand" aria-hidden="true">
          <img src={daivLogoNoText} alt="" className="daiv-cookie-banner-logo" width={40} height={40} />
        </div>
        <div className="daiv-cookie-banner-main">
          <div className="daiv-cookie-banner-copy">
            <p id="daiv-cookie-banner-title" className="daiv-cookie-banner-lead">
              <span className="daiv-cookie-banner-icon" aria-hidden>
                🌸
              </span>
              {compactRow ? t('consent.bannerLeadShort', lang) : t('consent.bannerLead', lang)}
            </p>
            {!compactRow && (
              <p className="daiv-cookie-banner-detail">
                {t('consent.bannerSub', lang)}{' '}
                {t('consent.privacyNote', lang)}
                <Link to="/privacy-policy" className="daiv-cookie-banner-link">
                  {t('consent.privacyLink', lang)}
                </Link>
                .
              </p>
            )}
          </div>
          <div className="daiv-cookie-banner-actions">
            <button type="button" className="daiv-cookie-btn daiv-cookie-btn--secondary" onClick={reject}>
              {compactRow ? t('consent.rejectShort', lang) : t('consent.reject', lang)}
            </button>
            <button type="button" className="daiv-cookie-btn daiv-cookie-btn--primary" onClick={accept}>
              {compactRow ? t('consent.acceptShort', lang) : t('consent.accept', lang)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
